import {
  VStack,
  Input,
  Box,
  Text,
  Field,
  Grid,
  GridItem,
  Button,
  Checkbox,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState, useRef } from "react";
import { useRoomStore, usePlacedModelsStore, useUserInterfaceStore, useModelStore } from "../../store";
import { defaultDecorOptions } from "../../constants/materialOptions";
import ImageUploadAndPreview from "./ImageUploadAndPreview";
import VideoUploadForDecoration from "./VideoUploadForDecoration";
import { useShallow } from "zustand/shallow";
import { convertToEmbedUrl, isValidYouTubeUrl, extractYouTubeVideoId } from "../../utils/youtubeHelpers";
import { convertToSpotifyEmbedUrl, isValidSpotifyUrl } from "../../utils/spotifyHelpers";
import { buildTwitchEmbedUrl, extractTwitchChannelName, isValidTwitchChannelName } from "../../utils/twitchHelpers";
import { textureCache } from "../../services/textureCache";
import { getDecorationPlacementForCamera } from "../../constants/Helpers";
import { containsProfanity } from "../../utils/profanityFilter";
import { track, Events } from "../../utils/analytics";

function RoomEditorTools() {
  const [_link, setLink] = useState("");
  const [hoverText, setHoverText] = useState("");
  const [value, setValue] = useState("image");
  const [error, setError] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeError, setYoutubeError] = useState("");
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [spotifyError, setSpotifyError] = useState("");
  const [twitchChannel, setTwitchChannel] = useState("");
  const [twitchError, setTwitchError] = useState("");
  const [spotifyThumbnail, setSpotifyThumbnail] = useState(null);
  const [hasUnsavedLinkChanges, setHasUnsavedLinkChanges] = useState(false);
  const [hoverTextError, setHoverTextError] = useState("");
  const isUpdating = useRef(false);

  const decorations = useRoomStore((s) => s.decorations);
  const addDecoration = useRoomStore((s) => s.addDecoration);
  const scaleX = useRoomStore((s) => s.scaleX);
  const scaleZ = useRoomStore((s) => s.scaleZ);
  const height = useRoomStore((s) => s.height);
  const setSelectedDecoration = useUserInterfaceStore((s) => s.setSelectedDecoration);
  const cameraControls = useModelStore((s) => s.cameraControls);

  const {
    editingRoom,
    decorationType,
    decorationOption,
    selectedDecorationIndex,
    setDecorationType,
    setDecorationLink,
    setDecorationHoverText,
    setDecorationOption,
    showLinkIndicators,
    setShowLinkIndicators,
  } = useUserInterfaceStore(
    useShallow((s) => ({
      editingRoom: s.editingRoom,
      decorationType: s.decorationType,
      decorationOption: s.decorationOption,
      selectedDecorationIndex: s.selectedDecorationIndex,
      setDecorationType: s.setDecorationType,
      setDecorationLink: s.setDecorationLink,
      setDecorationHoverText: s.setDecorationHoverText,
      setDecorationOption: s.setDecorationOption,
      showLinkIndicators: s.showLinkIndicators,
      setShowLinkIndicators: s.setShowLinkIndicators,
    }))
  );

  // Compute YouTube thumbnail from decorationOption (embed URL)
  const youtubeThumbnail = useMemo(() => {
    if (decorationOption && typeof decorationOption === "string" && decorationOption.includes("youtube")) {
      const videoId = extractYouTubeVideoId(decorationOption);
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
      }
    }
    return null;
  }, [decorationOption]);

  // Fetch Spotify thumbnail when decorationOption changes
  useEffect(() => {
    if (decorationOption && typeof decorationOption === "string" && decorationOption.includes("spotify")) {
      const regularUrl = decorationOption.replace("/embed/", "/");
      fetch(`https://open.spotify.com/oembed?url=${encodeURIComponent(regularUrl)}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.thumbnail_url) {
            setSpotifyThumbnail(data.thumbnail_url);
          }
        })
        .catch(() => setSpotifyThumbnail(null));
    } else {
      setSpotifyThumbnail(null);
    }
  }, [decorationOption]);

  const updateDecorationProperty = useRoomStore(
    (s) => s.updateDecorationProperty
  );

  const safeUpdateDecoration = (key, value) => {
    if (selectedDecorationIndex > -1) {
      isUpdating.current = true;
      updateDecorationProperty(selectedDecorationIndex, key, value);
      // Reset the flag after a brief delay to allow state updates to settle
      setTimeout(() => {
        isUpdating.current = false;
      }, 100);
    }
  };

  const decorationTypes = useMemo(
    () => [
      { label: "Color", value: "color" },
      { label: "Image", value: "image" },
      { label: "Video", value: "video" },
      { label: "YouTube", value: "youtube" },
      { label: "Spotify", value: "spotify" },
      { label: "Twitch", value: "twitch" },
    ],
    []
  );

  useEffect(() => {
    if (decorationType) {
      setValue(decorationType);
    }
  }, [decorationType]);

  // Preload image textures when selected to ensure they show during drag preview
  useEffect(() => {
    if (decorationType === "image" && decorationOption?.url) {
      const imageUrl = decorationOption.url;
      if (imageUrl && typeof imageUrl === "string" && imageUrl.trim() !== "") {
        // Preload the texture into the cache if not already cached
        if (!textureCache.has(imageUrl)) {
          textureCache.load(imageUrl).catch((err) => {
            console.warn("Failed to preload texture:", imageUrl, err);
          });
        }
      }
    }
  }, [decorationType, decorationOption]);

  // Handle decoration selection changes - load or clear fields appropriately
  // Only triggers when selectedDecorationIndex changes, not when decoration content updates
  useEffect(() => {
    // Skip if we're currently updating to prevent circular updates
    if (isUpdating.current) return;

    if (selectedDecorationIndex > -1 && selectedDecorationIndex < decorations.length) {
      // Load the selected decoration's data from current store state
      const decoration = decorations[selectedDecorationIndex];
      setHoverText(decoration.hoverText || "");
      setLink(decoration.link || "");
      setHasUnsavedLinkChanges(false); // Reset unsaved changes when loading a decoration

      // Load YouTube URL if it's a YouTube decoration
      if (decoration.type === "youtube" && decoration.option) {
        setYoutubeUrl(decoration.option);
      }

      // Load Spotify URL if it's a Spotify decoration
      if (decoration.type === "spotify" && decoration.option) {
        setSpotifyUrl(decoration.option);
      }

      // Load Twitch channel if it's a Twitch decoration
      if (decoration.type === "twitch" && decoration.option) {
        // Extract channel name from stored embed URL
        const match = decoration.option.match(/[?&]channel=([^&]+)/);
        setTwitchChannel(match ? match[1] : "");
      }

      // Only update decoration type if it's different to avoid triggering other effects
      if (decoration.type && decoration.type !== decorationType) {
        setValue(decoration.type);
        setDecorationType(decoration.type);
        setDecorationOption(decoration.option);
      } else if (decoration.type) {
        setDecorationOption(decoration.option);
      }
    } else if (selectedDecorationIndex === -1) {
      // Clear fields when no decoration is selected
      setDecorationHoverText("");
      setHoverText("");
      setLink("");
      setDecorationLink("");
      setError("");
      setYoutubeUrl("");
      setYoutubeError("");
      setSpotifyUrl("");
      setSpotifyError("");
      setTwitchChannel("");
      setTwitchError("");
      setHasUnsavedLinkChanges(false); // Reset unsaved changes when clearing
      // Clear decoration option for youtube/spotify/twitch to remove stale thumbnails
      if (decorationType === "youtube" || decorationType === "spotify" || decorationType === "twitch") {
        setDecorationOption(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDecorationIndex,
    // Only trigger when selection index changes, not when decoration content changes
  ]);

  const handleDecorationTypeSelect = (type) => {
    if (selectedDecorationIndex < 0) {
      setValue(type);
      setDecorationType(type);
      setDecorationOption(defaultDecorOptions[type] || "orange");
      safeUpdateDecoration("type", type);
      safeUpdateDecoration("option", defaultDecorOptions[type] || "orange");
      // Clear URL fields when switching types
      setYoutubeUrl("");
      setYoutubeError("");
      setSpotifyUrl("");
      setSpotifyError("");
      setTwitchChannel("");
      setTwitchError("");
    }
  };

  const handleSaveLink = () => {
    // If YouTube type and no decoration selected, create a new decoration
    if (value === "youtube" && selectedDecorationIndex < 0) {
      if (!youtubeUrl.trim()) {
        setYoutubeError("Please enter a YouTube URL");
        return;
      }

      if (!isValidYouTubeUrl(youtubeUrl)) {
        setYoutubeError("Please enter a valid YouTube URL (youtube.com or youtu.be)");
        return;
      }

      const embedUrl = convertToEmbedUrl(youtubeUrl);
      if (!embedUrl) {
        setYoutubeError("Could not convert YouTube URL to embed format");
        return;
      }

      setYoutubeError("");
      setDecorationOption(embedUrl);

      // Default size for YouTube embed
      const defaultWidth = 30;
      const defaultHeight = defaultWidth * (9 / 16); // 16:9 aspect ratio

      // Add random offset so multiple don't stack exactly
      const randomX = (Math.random() - 0.5) * 30;
      const randomY = (Math.random() - 0.5) * 20;

      // Place on the wall the camera is currently facing
      const placement = getDecorationPlacementForCamera(
        cameraControls, scaleX, scaleZ, height, randomX, randomY
      );

      const decoration = {
        type: "youtube",
        option: embedUrl,
        position: placement.position,
        rotation: placement.rotation,
        width: defaultWidth,
        height: defaultHeight,
        link: "",
        hoverText: hoverText || "",
      };

      const currentDecorations = useRoomStore.getState().decorations;
      const isFirstDecoration = currentDecorations.length === 0;
      const roomId = usePlacedModelsStore.getState()._roomId;

      console.log("📺 Auto-creating YouTube decoration:", decoration);
      addDecoration(decoration);

      if (isFirstDecoration) {
        track(Events.DECORATION_ADDED, { roomId, decorationType: 'youtube' });
      }

      // Select the newly created decoration
      setTimeout(() => {
        const newIndex = useRoomStore.getState().decorations.length - 1;
        console.log("🎯 Selecting new YouTube decoration at index:", newIndex);
        setSelectedDecoration(decoration, newIndex);
      }, 50);

      // Clear the URL field for next entry
      setYoutubeUrl("");
      setHoverText("");
      setHasUnsavedLinkChanges(false);
      return;
    }

    // If Twitch type and no decoration selected, create a new decoration
    if (value === "twitch" && selectedDecorationIndex < 0) {
      if (!twitchChannel.trim()) {
        setTwitchError("Please enter a Twitch channel name");
        return;
      }

      const channelName = extractTwitchChannelName(twitchChannel);
      if (!channelName || !isValidTwitchChannelName(channelName)) {
        setTwitchError("Please enter a valid Twitch channel name");
        return;
      }

      const embedUrl = buildTwitchEmbedUrl(channelName);
      setTwitchError("");
      setDecorationOption(embedUrl);

      const defaultWidth = 30;
      const defaultHeight = defaultWidth * (9 / 16);

      const randomX = (Math.random() - 0.5) * 30;
      const randomY = (Math.random() - 0.5) * 20;

      const placement = getDecorationPlacementForCamera(
        cameraControls, scaleX, scaleZ, height, randomX, randomY
      );

      const decoration = {
        type: "twitch",
        option: embedUrl,
        position: placement.position,
        rotation: placement.rotation,
        width: defaultWidth,
        height: defaultHeight,
        link: "",
        hoverText: hoverText || "",
      };

      const currentDecorationsTwitch = useRoomStore.getState().decorations;
      const isFirstDecorationTwitch = currentDecorationsTwitch.length === 0;
      const roomIdTwitch = usePlacedModelsStore.getState()._roomId;

      addDecoration(decoration);

      if (isFirstDecorationTwitch) {
        track(Events.DECORATION_ADDED, { roomId: roomIdTwitch, decorationType: 'twitch' });
      }

      setTimeout(() => {
        const newIndex = useRoomStore.getState().decorations.length - 1;
        setSelectedDecoration(decoration, newIndex);
      }, 50);

      setTwitchChannel("");
      setHoverText("");
      setHasUnsavedLinkChanges(false);
      return;
    }

    // If Spotify type and no decoration selected, create a new decoration
    if (value === "spotify" && selectedDecorationIndex < 0) {
      if (!spotifyUrl.trim()) {
        setSpotifyError("Please enter a Spotify URL");
        return;
      }

      if (!isValidSpotifyUrl(spotifyUrl)) {
        setSpotifyError("Please enter a valid Spotify URL");
        return;
      }

      const embedUrl = convertToSpotifyEmbedUrl(spotifyUrl);
      if (!embedUrl) {
        setSpotifyError("Could not convert Spotify URL to embed format");
        return;
      }

      setSpotifyError("");
      setDecorationOption(embedUrl);

      // Default size for Spotify embed - compact player is 152px height
      // iframe uses height * 40 for pixels, so 152/40 ≈ 3.8
      const defaultWidth = 25;
      const defaultHeight = 3.8; // Matches Spotify compact embed height (152px)

      // Add random offset so multiple don't stack exactly
      const randomX = (Math.random() - 0.5) * 30;
      const randomY = (Math.random() - 0.5) * 20;

      // Place on the wall the camera is currently facing
      const placement = getDecorationPlacementForCamera(
        cameraControls, scaleX, scaleZ, height, randomX, randomY
      );

      const decoration = {
        type: "spotify",
        option: embedUrl,
        position: placement.position,
        rotation: placement.rotation,
        width: defaultWidth,
        height: defaultHeight,
        link: "",
        hoverText: hoverText || "",
      };

      const currentDecorationsSpotify = useRoomStore.getState().decorations;
      const isFirstDecorationSpotify = currentDecorationsSpotify.length === 0;
      const roomIdSpotify = usePlacedModelsStore.getState()._roomId;

      console.log("🎵 Auto-creating Spotify decoration:", decoration);
      addDecoration(decoration);

      if (isFirstDecorationSpotify) {
        track(Events.DECORATION_ADDED, { roomId: roomIdSpotify, decorationType: 'spotify' });
      }

      // Select the newly created decoration
      setTimeout(() => {
        const newIndex = useRoomStore.getState().decorations.length - 1;
        console.log("🎯 Selecting new Spotify decoration at index:", newIndex);
        setSelectedDecoration(decoration, newIndex);
      }, 50);

      // Clear the URL field for next entry
      setSpotifyUrl("");
      setHoverText("");
      setHasUnsavedLinkChanges(false);
      return;
    }

    // Check hover text for profanity
    if (hoverText && containsProfanity(hoverText)) {
      setHoverTextError("Hover text contains inappropriate language");
      return;
    }
    setHoverTextError("");

    // Save link if present
    if (_link) {
      setDecorationLink(_link);
      safeUpdateDecoration("link", _link);
      setError("");
    }
    // Save hover text (can be empty)
    setDecorationHoverText(hoverText);
    safeUpdateDecoration("hoverText", hoverText);
    // Mark as saved
    setHasUnsavedLinkChanges(false);
  };

  if (!editingRoom) {
    return null;
  }

  return (
    <VStack spacing={4} align="stretch">
      {editingRoom && (
        <>
          {/* Decoration Type Selection - 2x2 Button Grid */}

          <>
            <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
              Select Decoration Type:
            </Text>
            <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={4}>
              {decorationTypes.map((type) => {
                const isActive = value === type.value;
                const disabled = selectedDecorationIndex >= 0;

                return (
                  <GridItem key={type.value}>
                    <Box
                      onClick={() => handleDecorationTypeSelect(type.value)}
                      cursor="pointer"
                      height="40px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      border="2px solid"
                      borderColor={isActive ? "#26EF96" : "gray.600"}
                      bg={isActive ? "rgba(38, 239, 150, 0.1)" : "gray.800"}
                      _hover={
                        !disabled
                          ? {
                              bg: isActive
                                ? "rgba(38, 239, 150, 0.15)"
                                : "gray.700",
                              borderColor: isActive ? "#26EF96" : "gray.500",
                            }
                          : {}
                      }
                      transition="all 0.2s"
                      borderRadius="0"
                    >
                      <Text
                        color={
                          isActive ? "#26EF96" : disabled ? "gray.600" : "white"
                        }
                        fontWeight={isActive ? "bold" : "medium"}
                        fontSize="md"
                      >
                        {type.label}
                      </Text>
                    </Box>
                  </GridItem>
                );
              })}
            </Grid>
          </>

          {/* Decoration Options based on selected type */}
          <Box>
            {value === "color" && (
              <Input
                type="color"
                defaultValue={"#ffffff"}
                value={decorationOption ? decorationOption : "#ffffff"}
                onChange={(e) => {
                  const val = e.target.value;
                  setDecorationOption(val);
                  safeUpdateDecoration("option", val);
                }}
                borderRadius="0"
                bg="gray.800"
                borderColor="gray.600"
                _focus={{ borderColor: "#26EF96" }}
              />
            )}

            {value === "image" && (
              <ImageUploadAndPreview
                selectedValue={decorationOption}
                setSelectedValue={(val) => {
                  setDecorationOption(val);
                  safeUpdateDecoration("option", val);
                }}
              />
            )}

            {value === "video" && (
              <VideoUploadForDecoration
                setSelectedValue={(val) => {
                  setDecorationOption(val);
                  safeUpdateDecoration("option", val);
                }}
              />
            )}

            {value === "youtube" && (
              <Box>
                <Text fontSize="xs" mb={1} color="gray.400">
                  YouTube Video URL:
                </Text>
                <Field.Root invalid={!!youtubeError}>
                  <Input
                    size="sm"
                    value={youtubeUrl}
                    onChange={(e) => {
                      setYoutubeUrl(e.target.value);
                      if (youtubeError) setYoutubeError("");
                    }}
                    onBlur={() => {
                      if (youtubeUrl.trim()) {
                        if (isValidYouTubeUrl(youtubeUrl)) {
                          const embedUrl = convertToEmbedUrl(youtubeUrl);
                          if (embedUrl) {
                            setDecorationOption(embedUrl);
                            safeUpdateDecoration("option", embedUrl);
                            setYoutubeError("");
                          } else {
                            setYoutubeError("Could not convert YouTube URL to embed format");
                          }
                        } else {
                          setYoutubeError("Please enter a valid YouTube URL (youtube.com or youtu.be)");
                        }
                      }
                    }}
                    placeholder="https://www.youtube.com/watch?v=..."
                    bg="gray.800"
                    borderColor="gray.600"
                    _focus={{ borderColor: "#26EF96" }}
                    borderRadius="0"
                  />
                  {youtubeError && <Field.ErrorText>{youtubeError}</Field.ErrorText>}
                </Field.Root>
                <Text fontSize="xs" mt={1} color="gray.500" fontStyle="italic">
                  Paste any YouTube URL (watch, youtu.be, or embed format)
                </Text>
              </Box>
            )}

            {value === "twitch" && (
              <Box>
                <Text fontSize="xs" mb={1} color="gray.400">
                  Twitch Channel Name:
                </Text>
                <Field.Root invalid={!!twitchError}>
                  <Input
                    size="sm"
                    value={twitchChannel}
                    onChange={(e) => {
                      setTwitchChannel(e.target.value);
                      if (twitchError) setTwitchError("");
                    }}
                    placeholder="channelname or twitch.tv/channelname..."
                    bg="gray.800"
                    borderColor="gray.600"
                    _focus={{ borderColor: "#26EF96" }}
                    borderRadius="0"
                  />
                  {twitchError && <Field.ErrorText>{twitchError}</Field.ErrorText>}
                </Field.Root>
                <Text fontSize="xs" mt={1} color="gray.500" fontStyle="italic">
                  Enter their channel name — shows offline screen until they go live
                </Text>
              </Box>
            )}

            {value === "spotify" && (
              <Box>
                <Text fontSize="xs" mb={1} color="gray.400">
                  Spotify URL:
                </Text>
                <Field.Root invalid={!!spotifyError}>
                  <Input
                    size="sm"
                    value={spotifyUrl}
                    onChange={(e) => {
                      setSpotifyUrl(e.target.value);
                      if (spotifyError) setSpotifyError("");
                    }}
                    onBlur={() => {
                      if (spotifyUrl.trim()) {
                        if (isValidSpotifyUrl(spotifyUrl)) {
                          const embedUrl = convertToSpotifyEmbedUrl(spotifyUrl);
                          if (embedUrl) {
                            setDecorationOption(embedUrl);
                            safeUpdateDecoration("option", embedUrl);
                            setSpotifyError("");
                          } else {
                            setSpotifyError("Could not convert Spotify URL to embed format");
                          }
                        } else {
                          setSpotifyError("Please enter a valid Spotify URL");
                        }
                      }
                    }}
                    placeholder="https://open.spotify.com/track/..."
                    bg="gray.800"
                    borderColor="gray.600"
                    _focus={{ borderColor: "#26EF96" }}
                    borderRadius="0"
                  />
                  {spotifyError && <Field.ErrorText>{spotifyError}</Field.ErrorText>}
                </Field.Root>
                <Text fontSize="xs" mt={1} color="gray.500" fontStyle="italic">
                  Paste any Spotify URL (track, album, playlist, artist, etc.)
                </Text>
              </Box>
            )}
          </Box>

          {/* Link and Hover Text Inputs - Hidden for youtube/spotify/twitch since URL is the content */}
          {value !== "youtube" && value !== "spotify" && value !== "twitch" && (
            <Box w="100%">
              <Text fontSize="xs" mb={1} color="gray.400">
                Add clickable link:
              </Text>
              <Field.Root invalid={!!error}>
                <Input
                  size="sm"
                  value={_link || ""}
                  onChange={(e) => {
                    setLink(e.target.value);
                    setHasUnsavedLinkChanges(true);
                    if (error) setError("");
                  }}
                  placeholder="Type Link here..."
                  bg="gray.800"
                  borderColor="gray.600"
                  _focus={{ borderColor: "#26EF96" }}
                  borderRadius="0"
                />
                {error && <Field.ErrorText>{error}</Field.ErrorText>}
              </Field.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Links are protected by Google Safe Browsing API
              </Text>
            </Box>
          )}

          {/* Hover Text - Available for all decoration types */}
          <Box w="100%">
            <Text fontSize="xs" mb={1} color="gray.400">
              Add Hover Text:
            </Text>
            <Field.Root invalid={!!hoverTextError}>
              <Input
                size="sm"
                value={hoverText || ""}
                onChange={(e) => {
                  setHoverText(e.target.value);
                  setHasUnsavedLinkChanges(true);
                  if (hoverTextError) setHoverTextError("");
                }}
                placeholder="Type Hover Text here..."
                bg="gray.800"
                borderColor="gray.600"
                _focus={{ borderColor: "#26EF96" }}
                borderRadius="0"
              />
              {hoverTextError && <Field.ErrorText>{hoverTextError}</Field.ErrorText>}
            </Field.Root>
          </Box>

          {/* Add Link Button */}
          <Box w="100%">
            <Button
              size="sm"
              width="100%"
              onClick={handleSaveLink}
              bg={hasUnsavedLinkChanges ? "#26EF96" : "gray.600"}
              color={hasUnsavedLinkChanges ? "black" : "white"}
              _hover={{
                bg: hasUnsavedLinkChanges ? "#1fd47f" : "gray.500",
              }}
              borderRadius="0"
              fontWeight="medium"
            >
              Add Link
            </Button>
            <Checkbox.Root
              mt={2}
              checked={showLinkIndicators}
              onCheckedChange={(e) => setShowLinkIndicators(e.checked)}
              colorPalette="green"
              size="sm"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control borderColor="gray.500">
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label color="gray.400" fontSize="xs">
                Show link indicators
              </Checkbox.Label>
            </Checkbox.Root>
          </Box>

          {/* YouTube Thumbnail Preview */}
          {value === "youtube" && youtubeThumbnail && youtubeUrl && (
            <Box borderRadius="4px" overflow="hidden" border="1px solid" borderColor="gray.600">
              <img
                src={youtubeThumbnail}
                alt="YouTube video thumbnail"
                style={{ width: "100%", display: "block" }}
              />
            </Box>
          )}

          {/* Spotify Thumbnail Preview */}
          {value === "spotify" && spotifyThumbnail && spotifyUrl && (
            <Box borderRadius="4px" overflow="hidden" border="1px solid" borderColor="gray.600">
              <img
                src={spotifyThumbnail}
                alt="Spotify cover art"
                style={{ width: "100%", display: "block" }}
              />
            </Box>
          )}

          {/* Instruction Text */}
          <Text fontSize="sm" mt={2} color="gray.500" fontStyle="italic">
            Click and drag on the wall to add decorations
          </Text>
        </>
      )}
    </VStack>
  );
}

export default RoomEditorTools;
